import React from 'react'
import { Bar } from 'react-chartjs-2'
import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LinearScale,
	Tooltip,
} from 'chart.js'

import { IDwMonthlyCost, formatEurCost, formatMonth } from '../../Services/datawarehouse'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

interface Props {
	monthly: IDwMonthlyCost[]
}

// Évolution de la dépense Google sur douze mois.
//
// Barres EMPILÉES et non deux séries côte à côte : la question posée est « quelle
// part de ce qu'on paie concerne des projets clients ? », et c'est une
// décomposition d'un total, pas une comparaison de deux grandeurs.

// #region DwMonthlyChart
export const DwMonthlyChart: React.FC<Props> = ({ monthly }) => {
	const labels = monthly.map((m) => formatMonth(m.month))
	const attached = monthly.map((m) => Math.max(0, m.costEur - m.unattachedEur))
	const unattached = monthly.map((m) => m.unattachedEur)

	// ⚠️ Un mois partiel est ATTÉNUÉ, jamais masqué : le supprimer ferait un trou
	// dans la courbe, le laisser plein ferait lire une chute de la dépense là où
	// le mois n'est simplement pas fini (ou pas entièrement mesuré). Avril 2026,
	// mesuré sur 9 jours, vaut 154 € contre 549 € en mai — plein, il se lirait
	// comme un triplement.
	const fade = (solid: string, faded: string) =>
		monthly.map((m) => (m.partial ? faded : solid))

	return (
		<Bar
			height={90}
			data={{
				labels,
				datasets: [
					{
						label: 'Projets Quanti',
						data: attached,
						backgroundColor: fade('rgba(46,125,50,0.85)', 'rgba(46,125,50,0.30)'),
						stack: 'cost',
					},
					{
						label: 'Hors projets Quanti (infrastructure)',
						data: unattached,
						backgroundColor: fade('rgba(2,136,209,0.75)', 'rgba(2,136,209,0.25)'),
						stack: 'cost',
					},
				],
			}}
			options={{
				responsive: true,
				maintainAspectRatio: true,
				scales: {
					x: { stacked: true, grid: { display: false } },
					y: {
						stacked: true,
						ticks: { callback: (v) => `${v} €` },
					},
				},
				plugins: {
					legend: { position: 'bottom' },
					tooltip: {
						callbacks: {
							label: (ctx) => `${ctx.dataset.label} : ${formatEurCost(Number(ctx.raw) || 0)}`,
							// ⚠️ La raison d'un mois bas se lit ICI, au survol : sans elle,
							// « 154 € » et « 154 € sur 9 jours mesurés » se ressemblent
							// exactement sur un graphe.
							afterBody: (items) => {
								const m = monthly[items[0].dataIndex]
								if (!m) return ''
								if (m.measuredDays === 0) return 'Aucune journée mesurée sur ce mois'
								if (m.partial) return `${m.measuredDays} jour(s) mesuré(s) sur ${m.daysInMonth}`
								return ''
							},
						},
					},
				},
			}}
		/>
	)
}

// #endregion
